import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, Lock } from "lucide-react";
import { errMsg, FIELD_LABELS, fmt, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { ErrorBox, Loading, StatusBadge } from "../../components/kit";
import { ProfileFacts, ProfileHeader } from "../VendorPublic";
import { ConsentPanel } from "./ConsentPanel";

function ShareStep({ d, requestId, reload }: { d: any; requestId: string; reload: () => void }) {
  const [open, setOpen] = useState(false);
  const c = d.candidate;
  if (!c.engaged) return <p className="text-sm text-[var(--muted)]">Available after you engage this vendor.</p>;
  if (d.active_consent) {
    return (
      <div className="text-sm" data-testid="candidate-consent-active">
        <p>Shared: <strong>{d.active_consent.fields.map((f: string) => FIELD_LABELS[f]).join(", ")}</strong> until {fmt(d.active_consent.expires_at)}.</p>
        {d.introduction && <p className="mt-1">Vendor response: <StatusBadge value={d.introduction.status} testId="candidate-intro-status" /></p>}
      </div>
    );
  }
  if (d.request.status !== "open") return <p className="text-sm">This request is {d.request.status}.</p>;
  if (open) return <ConsentPanel requestId={requestId} candidate={c} onCancel={() => setOpen(false)} onDone={() => { setOpen(false); reload(); }} />;
  return (
    <>
      <button className="btn btn-primary btn-lg w-full" onClick={() => setOpen(true)} data-testid="candidate-request-button">Request This Contractor</button>
      <p className="mt-2 text-center text-xs text-[var(--muted)]">Your contact information will be shared only after you choose the exact fields and purpose.</p>
    </>
  );
}

export default function CandidatePage() {
  const { id, cid } = useParams();
  const api = useApi();
  const { catName, areaName } = useTaxonomy();
  const { data, loading, error, reload } = useLoad<any>(`/customer/requests/${id}/candidates/${cid}`);
  const engage = async () => {
    try { await api("post", `/customer/requests/${id}/engagements`, { candidate_id: cid }); toast.success("Vendor identity revealed to you only. Nothing about you was shared."); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const back = <Link to={`/customer/requests/${id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--blue)]" data-testid="candidate-back-link"><ArrowLeft className="h-4 w-4" />Back to Results</Link>;
  if (loading && !data) return <div className="mx-auto max-w-3xl px-4"><Loading /></div>;
  if (error) return <div className="mx-auto max-w-3xl space-y-4 px-4 py-8">{back}<ErrorBox message={error === "Not found" ? "This match is no longer available." : error} onRetry={reload} /></div>;
  if (!data) return null;
  const c = data.candidate;
  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-8" data-testid="candidate-page">
      {back}
      <h1 className="text-3xl font-extrabold text-[var(--navy)]">Contractor Profile</h1>
      <ProfileHeader v={c} title={c.engaged ? c.vendor_name : c.trade_label} />
      <section className="card space-y-3">
        <h2 className="text-lg font-bold">About This Contractor</h2>
        {c.engaged && <p className="text-sm font-semibold">{c.trade_label}</p>}
        <p className="text-sm text-[#3b4660]">{c.description || c.headline}</p>
        <ProfileFacts v={c} catName={catName} areaName={areaName} />
      </section>
      <section className="card space-y-3" aria-labelledby="step1">
        <h2 id="step1" className="flex items-center gap-2 text-lg font-bold"><Eye className="h-5 w-5 text-[var(--blue)]" aria-hidden />Step 1 · Engage to see who this is</h2>
        {c.engaged ? (
          <p className="text-sm text-[#146c3a]" data-testid="candidate-engaged-notice">You engaged <strong data-testid="candidate-vendor-name">{c.vendor_name}</strong>. They have not been notified and have none of your details.</p>
        ) : (
          <>
            <p className="text-sm text-[var(--muted)]">Reveals the business name to you only. Nothing about you is shared and the vendor is not notified.</p>
            <button className="btn btn-dark" onClick={engage} disabled={data.request.status !== "open"} data-testid="candidate-engage-button">Engage &amp; reveal vendor</button>
          </>
        )}
      </section>
      <section className="card space-y-3" aria-labelledby="step2">
        <h2 id="step2" className="flex items-center gap-2 text-lg font-bold"><Lock className="h-5 w-5 text-[var(--red)]" aria-hidden />Step 2 · Share your details (separate consent)</h2>
        <ShareStep d={data} requestId={id} reload={reload} />
      </section>
      <Link to={`/customer/requests/${id}`} className="btn btn-ghost w-full" data-testid="candidate-choose-another-link">Choose another vendor</Link>
    </div>
  );
}
